import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { ChevronRight } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { type TrackingProgress, type PriorityTrackingCard, type TrackingItem } from '../../../../../models/TrackingProgress'
import { GetTrackingProgress } from '../../../../../services/SurveyService'
import { handleApiResponse } from '../../../../../utils/apiHelpers'

interface Props {
  planOrganizationId: number
}

interface CardStyleConfig {
  headerBg: string
  bodyBg: string
  borderColor: string
}

const CARD_STYLES: Record<number, CardStyleConfig> = {
  1: {
    headerBg: 'var(--Theme-primitive-primary-500, #0067a5)',
    bodyBg: 'var(--Theme-primitive-primary-50, #e6f0f6)',
    borderColor: 'var(--Theme-primitive-primary-500, #0067a5)'
  },
  2: {
    headerBg: 'var(--Theme-primitive-secondary-500, #7666a8)',
    bodyBg: 'var(--Theme-primitive-secondary-50, #f1f0f6)',
    borderColor: 'var(--Theme-primitive-secondary-500, #7666a8)'
  },
  3: {
    headerBg: 'var(--Feedback-primitive-success-700, #638d2d)',
    bodyBg: 'var(--Feedback-primitive-success-50, #f4f9ec)',
    borderColor: 'var(--Feedback-primitive-success-700, #638d2d)'
  },
  4: {
    headerBg: 'var(--Theme-primitive-tertiary-700, #177c9e)',
    bodyBg: 'var(--Theme-primitive-primary-50, #e6f0f6)',
    borderColor: 'var(--Theme-primitive-tertiary-700, #177c9e)'
  }
}

const LINK_COLOR = '#5499c3'

interface TrackingItemRowProps {
  item: TrackingItem
  onNavigate?: () => void
}

const TrackingItemRow = ({ item, onNavigate }: TrackingItemRowProps): JSX.Element => (
  <Stack sx={{ gap: 0, minWidth: 0 }}>
    <Box
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        onNavigate?.()
      }}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        textAlign: 'left',
        cursor: onNavigate != null ? 'pointer' : 'default'
      }}
    >
      <Typography
        variant='body2'
        sx={{
          fontWeight: 600,
          color: LINK_COLOR,
          textDecoration: 'underline',
          fontSize: '14px',
          lineHeight: 1.4,
          minWidth: 0,
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          whiteSpace: 'normal',
          width: '100%'
        }}
      >
        {item.label}
      </Typography>
      <ChevronRight sx={{ color: LINK_COLOR, fontSize: '24px', flexShrink: 0 }} />
    </Box>
    <Typography
      variant='body2'
      sx={{
        fontSize: '14px',
        lineHeight: 1.4,
        color: 'var(--BaseTones-primitive-nuetral-900, #252426)'
      }}
    >
      {item.tracked} of {item.total} Tracked
    </Typography>
  </Stack>
)

interface TrackingCardProps {
  card: PriorityTrackingCard
  onItemNavigate: (planOrgTaskId: number, activityName: string) => void
}

const TrackingCard = ({ card, onItemNavigate }: TrackingCardProps): JSX.Element => {
  const style = CARD_STYLES[card.priorityNumber] ?? CARD_STYLES[1]

  return (
    <Box sx={{
      width: '260px',
      minWidth: '260px',
      maxWidth: '260px',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}
    >
      <Box sx={{
        backgroundColor: style.headerBg,
        padding: '16px',
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        minHeight: '57px',
        overflow: 'hidden'
      }}
      >
        <Typography
          sx={{
            color: 'white',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '20px',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'normal',
            width: '100%'
          }}
        >
          {card.title}
        </Typography>
      </Box>
      <Stack sx={{
        backgroundColor: style.bodyBg,
        border: `5px solid ${style.borderColor}`,
        borderTop: 'none',
        borderBottomLeftRadius: '12px',
        borderBottomRightRadius: '12px',
        padding: '16px',
        gap: '16px',
        flex: 1,
        overflow: 'hidden'
      }}
      >
        {card.items.map((item) => (
          <TrackingItemRow
            key={item.label}
            item={item}
            onNavigate={() => { onItemNavigate(card.planOrgTaskId, item.activityName ?? item.label) }}
          />
        ))}
      </Stack>
    </Box>
  )
}

export const SurveyTrackingCardsContent = ({ planOrganizationId }: Props): JSX.Element => {
  const [progress, setProgress] = useState<TrackingProgress | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchProgress = async (): Promise<void> => {
      if (planOrganizationId === 0) {
        setLoading(false)
        return
      }
      const result = await handleApiResponse(GetTrackingProgress(planOrganizationId))
      setProgress(result ?? null)
      setLoading(false)
    }

    void fetchProgress()
  }, [planOrganizationId])

  const handleItemNavigate = (planOrgTaskId: number, activityName: string): void => {
    if (progress == null) return
    const { rcnId, planId, planOrgId } = progress
    const path = `/Implementation/${rcnId}/${planId}/3/PlanOrganization/${planOrgId}/Priority/${planOrgTaskId}?activity=${encodeURIComponent(activityName)}`
    navigate(path)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (progress == null || progress.priorities.length === 0) {
    return <></>
  }

  return (
    <Stack direction='row' gap='16px' alignItems='stretch' sx={{ pb: '40px' }}>
      {progress.priorities.map((card) => (
        <TrackingCard key={card.priorityNumber} card={card} onItemNavigate={handleItemNavigate} />
      ))}
    </Stack>
  )
}
